import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { BankAccountRepository } from '~/repositories/bankAccountRepository';
import { createMutation, createTriggerResource } from '~/utils/resource';

export const [BankAccountReconciliationProvider, useBankAccountReconciliation] = createMagicDoorContext('BankAccountReconciliation', () => {
  const repo = new BankAccountRepository();

  const [reconciliations, setReconciliations] = createSignal<MagicDoor.Api.ReconciliationPaginationDto | undefined>(undefined);
  const [currentReconciliation, setCurrentReconciliation] = createSignal<MagicDoor.Api.ReconciliationDto | undefined>(undefined);
  const [lastReconciliation, setLastReconciliation] = createSignal<MagicDoor.Api.ReconciliationDto | undefined>(undefined);
  const [reconciliationCandidates, setReconciliationCandidates] = createSignal<MagicDoor.Api.ReconciliationCandidateDto[] | undefined>(
    undefined
  );
  const [isInitialLoading, setIsInitialLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);
  const [bankAccountId, setBankAccountId] = createSignal<string | undefined>(undefined);
  const [reconciliationId, setReconciliationId] = createSignal<string | undefined>(undefined);

  const [reconciliationsResource, getReconciliations] = createTriggerResource(async (bankAccountId: string) => {
    const pagination = await repo.getBankAccountReconciliations(bankAccountId);
    return {
      items: pagination.items,
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
      totalPages: pagination.totalPages,
      totalCount: pagination.totalCount,
    };
  });

  const fetchReconciliations = createMutation(async (bankAccountId: string) => {
    if (!bankAccountId) return;
    try {
      setError(undefined);
      const data = await repo.getBankAccountReconciliations(bankAccountId);
      setReconciliations(data);
      setBankAccountId(bankAccountId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const fetchReconciliation = async (bankAccountId: string, reconciliationId: string) => {
    if (!bankAccountId || !reconciliationId) return;
    try {
      setIsInitialLoading(true);
      setCurrentReconciliation(undefined);
      setError(undefined);
      const data = await repo.getBankAccountReconciliation(bankAccountId, reconciliationId);
      setCurrentReconciliation(data);
      setBankAccountId(bankAccountId);
      setReconciliationId(reconciliationId);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchLastReconciliation = createMutation(async (bankAccountId: string) => {
    if (!bankAccountId) return;
    try {
      setError(undefined);
      const data = await repo.getLastReconciliation(bankAccountId);
      setLastReconciliation(data);
      setBankAccountId(bankAccountId);
      return data;
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setLastReconciliation(undefined);
        setBankAccountId(bankAccountId);
        return undefined;
      }
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const startReconciliation = createMutation(async (bankAccountId: string, reconciliation: MagicDoor.Api.StartReconciliationDto) => {
    try {
      setError(undefined);
      const result = await repo.startReconciliation(bankAccountId, reconciliation);
      await fetchReconciliations(bankAccountId);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const resolveReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, resolution: MagicDoor.Api.ResolveReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.resolveReconciliation(bankAccountId, reconciliationId, resolution);
        if (currentReconciliation()?.id === reconciliationId) {
          setCurrentReconciliation(result);
        }
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const addTransactionToReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, entry: MagicDoor.Api.AddTransactionToReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.addTransactionToReconciliation(bankAccountId, reconciliationId, entry);
        if (currentReconciliation()?.id === reconciliationId) {
          setCurrentReconciliation(result);
        }

        if (reconciliationCandidates()) {
          const updatedCandidates =
            reconciliationCandidates()?.map((candidate) =>
              candidate.transactionId === entry.transactionId ? { ...candidate, isSelected: true } : candidate
            ) || [];
          setReconciliationCandidates(updatedCandidates);
        }

        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const removeTransactionFromReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, transactionId: string) => {
      try {
        setError(undefined);
        await repo.removeTransactionFromReconciliation(bankAccountId, reconciliationId, transactionId);

        if (currentReconciliation()?.id === reconciliationId) {
          const current = currentReconciliation();
          if (current) {
            const updatedEntries = current.entries?.filter((entry) => entry.id !== transactionId) || [];
            setCurrentReconciliation({
              ...current,
              entries: updatedEntries,
            });
          }
        }

        if (reconciliationCandidates()) {
          const updatedCandidates =
            reconciliationCandidates()?.map((candidate) =>
              candidate.transactionId === transactionId ? { ...candidate, isSelected: false } : candidate
            ) || [];
          setReconciliationCandidates(updatedCandidates);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const updateReconciliation = createMutation(
    async (bankAccountId: string, reconciliationId: string, data: MagicDoor.Api.UpdateReconciliationDto) => {
      try {
        setError(undefined);
        const result = await repo.updateReconciliation(bankAccountId, reconciliationId, data);
        if (currentReconciliation()?.id === reconciliationId) {
          setCurrentReconciliation(result);
        }
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }
  );

  const deleteReconciliation = createMutation(async (bankAccountId: string, reconciliationId: string) => {
    try {
      setError(undefined);
      await repo.deleteReconciliation(bankAccountId, reconciliationId);
      if (currentReconciliation()?.id === reconciliationId) {
        clearCurrentReconciliation();
      }
      await fetchReconciliations(bankAccountId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  });

  const fetchReconciliationCandidates = async (bankAccountId: string, reconciliationId: string) => {
    if (!bankAccountId || !reconciliationId) return;
    try {
      setError(undefined);
      const data = await repo.getReconciliationCandidates(bankAccountId, reconciliationId);

      setReconciliationCandidates(data.candidates);
      return data;
    } catch (err) {
      console.error('Error fetching reconciliation candidates:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setReconciliationCandidates([]);
      throw err;
    }
  };

  // Clear current reconciliation
  const clearCurrentReconciliation = () => {
    setCurrentReconciliation(undefined);
    setReconciliationId(undefined);
  };

  // Clear all state
  const clearState = () => {
    setReconciliations(undefined);
    setCurrentReconciliation(undefined);
    setLastReconciliation(undefined);
    setReconciliationCandidates(undefined);
    setBankAccountId(undefined);
    setReconciliationId(undefined);
    setError(undefined);
  };

  return {
    // State
    reconciliations,
    currentReconciliation,
    lastReconciliation,
    reconciliationCandidates,
    isInitialLoading,
    error,
    bankAccountId,
    reconciliationId,
    reconciliationsResource,

    // Actions
    fetchReconciliations,
    fetchReconciliation,
    fetchLastReconciliation,
    fetchReconciliationCandidates,
    startReconciliation,
    updateReconciliation,
    deleteReconciliation,
    resolveReconciliation,
    addTransactionToReconciliation,
    removeTransactionFromReconciliation,
    getReconciliations,
    clearCurrentReconciliation,
    clearState,
  };
});
